import { useState, useEffect, useCallback, useRef } from "react";
import {
  getVPSNotificationsConfig,
  saveVPSNotificationsConfig,
  testVPSNotificationEmail,
  testVPSNotificationWhatsapp,
  sendVPSNotificationTest,
} from "../../../services/vps.api";
import { toast } from "react-toastify";

/**
 * Hook personalizado para gestión de notificaciones VPS
 * Maneja todas las llamadas API y estados
 */
const useVPSNotifications = () => {
  const isMountedRef = useRef(true);
  const [loading, setLoading] = useState(false);
  /** Solo POST guardar: no mezclar con loading del GET inicial ni con fetchConfig en segundo plano */
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingWhatsApp, setTestingWhatsApp] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  // Ref de montaje: se marca true tras el primer paint (evita carreras con StrictMode / desmontaje).
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Obtener configuración de notificaciones VPS
   * @param {{ silent?: boolean }} options - silent=true: no toca `loading` (evita deshabilitar el botón Guardar si el GET se cuelga tras guardar)
   */
  const fetchConfig = useCallback(async (options = {}) => {
    const silent = Boolean(options.silent);
    if (!isMountedRef.current) return;
    try {
      if (isMountedRef.current && !silent) {
        setLoading(true);
        setError(null);
      }
      const { data } = await getVPSNotificationsConfig();
      if (isMountedRef.current) {
        if (data.success) {
          setConfig(data.config || null);
        } else {
          throw new Error(data.message || "Error obteniendo configuración de notificaciones");
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.message || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error obteniendo configuración de notificaciones:", err);
      }
    } finally {
      if (isMountedRef.current && !silent) {
        setLoading(false);
      }
    }
  }, []);

  /**
   * Guardar configuración de notificaciones VPS
   */
  const saveConfig = useCallback(async (configData) => {
    try {
      if (isMountedRef.current) setSaving(true);
      if (isMountedRef.current) setError(null);
      const { data } = await saveVPSNotificationsConfig(configData);

      if (!data || !data.success) {
        throw new Error((data && data.message) || "Error guardando configuración");
      }

      if (isMountedRef.current) {
        toast.success(data.message || "Configuración guardada exitosamente");
      }

      // Refresco sin tocar `loading`: antes el GET ponía loading=true y disabled={loading} bloqueaba el botón (o indefinido si el GET colgaba).
      fetchConfig({ silent: true }).catch(() => {});

      return data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Error desconocido";
      if (isMountedRef.current) {
        setError(errorMessage);
        console.error("Error guardando configuración:", err);
        toast.error(errorMessage);
      }
      return null;
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  }, [fetchConfig]);

  /**
   * Probar conexión de email
   * Ahora solo requiere smtpConfigId
   */
  const testEmailConnection = useCallback(async (emailData) => {
    if (!isMountedRef.current) return { success: false, message: "" };
    try {
      if (isMountedRef.current) setTestingEmail(true);
      if (isMountedRef.current) setError(null);
      const { data } = await testVPSNotificationEmail(emailData);
      if (isMountedRef.current) {
        return {
          success: data.success || false,
          message: data.message || "Resultado de prueba de email"
        };
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.message || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error probando conexión email:", err);
        return { success: false, message: errorMessage };
      }
    } finally {
      if (isMountedRef.current) setTestingEmail(false);
    }
    return { success: false, message: "" };
  }, []);

  /**
   * Probar conexión de WhatsApp
   * Ahora solo requiere whatsappId
   */
  const testWhatsAppConnection = useCallback(async (whatsappData) => {
    if (!isMountedRef.current) return { success: false, message: "" };
    try {
      if (isMountedRef.current) setTestingWhatsApp(true);
      if (isMountedRef.current) setError(null);
      const { data } = await testVPSNotificationWhatsapp(whatsappData);
      if (isMountedRef.current) {
        return {
          success: data.success || false,
          message: data.message || "Resultado de prueba de WhatsApp"
        };
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.message || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error probando conexión WhatsApp:", err);
        return { success: false, message: errorMessage };
      }
    } finally {
      if (isMountedRef.current) setTestingWhatsApp(false);
    }
    return { success: false, message: "" };
  }, []);

  /**
   * Enviar notificación de prueba
   * @param formData - Datos del formulario para enviar prueba sin guardar (opcional)
   */
  const sendTestNotification = useCallback(async (formData = null) => {
    if (!isMountedRef.current) return { success: false, sent: {}, errors: [] };
    try {
      if (isMountedRef.current) setSendingTest(true);
      if (isMountedRef.current) setError(null);
      
      // Enviar datos del formulario si se proporcionan (permite pruebas sin guardar)
      const { data } = await sendVPSNotificationTest(formData || {});
      
      if (isMountedRef.current) {
        if (data.success) {
          toast.success(data.message || "Notificación de prueba enviada exitosamente");
        } else {
          toast.warning(data.message || "Error enviando notificación de prueba");
        }
        return {
          success: data.success || false,
          sent: data.sent || {},
          errors: data.errors || []
        };
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.message || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error enviando notificación de prueba:", err);
        toast.error(errorMessage);
        return { success: false, sent: {}, errors: [errorMessage] };
      }
    } finally {
      if (isMountedRef.current) setSendingTest(false);
    }
    return { success: false, sent: {}, errors: [] };
  }, []);

  // Cargar configuración al montar
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return {
    loading,
    saving,
    error,
    config,
    testingEmail,
    testingWhatsApp,
    sendingTest,
    fetchConfig,
    saveConfig,
    testEmailConnection,
    testWhatsAppConnection,
    sendTestNotification
  };
};

export default useVPSNotifications;
